<template>
  <LoadingWrapper :state="status">
    <div class="flex flex-row flex-wrap items-center gap-2">
      <h2 class="text-title-large md:text-headline-large flex grow flex-row items-center gap-0.5">
        {{ $t('settings.organization') }} <BaseIcon>chevron_right</BaseIcon>
        {{ $t('settings.teams') }}
      </h2>
      <BaseTextButton size="M" color="tonalPrimary" icon="group_add" @click="openCreateDialog">
        {{ $t('settings.organizationTeams.create') }}
      </BaseTextButton>
    </div>

    <EmptyPage
      v-if="teamList.length === 0"
      :title="$t('settings.organizationTeams.empty.title')"
      :description="$t('settings.organizationTeams.empty.description')"
      icon="groups"
    />
    <ul v-else class="flex flex-col gap-0.5">
      <li
        v-for="team in teamList"
        :key="team.id"
        class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large overflow-hidden"
      >
        <RouterLink
          :to="{ name: 'settings-organization-team', params: { teamId: team.id } }"
          class="group relative flex w-full cursor-pointer flex-row items-center p-3 text-left"
        >
          <BaseTeamAvatar class="mr-4" :name="team.name" />
          <div class="flex min-w-0 grow flex-col">
            <span class="text-body-large font-medium">{{ team.name }}</span>
            <span class="text-body-medium text-on-surface-variant">{{
              $t('settings.organizationTeams.memberCount', { count: team.memberCount })
            }}</span>
          </div>
          <BaseIcon class="text-on-surface-variant">chevron_right</BaseIcon>
          <BaseStateLayer />
        </RouterLink>
      </li>
    </ul>

    <CreateTeamDialog
      ref="createDialog"
      :is-open="isCreateOpen"
      @close="closeCreateDialog"
      @create="onCreate"
    />
  </LoadingWrapper>
</template>
<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue'
  import { useRouter } from 'vue-router'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import BaseTeamAvatar from '@/core/components/base/avatar/BaseTeamAvatar.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import EmptyPage from '@/core/components/empty-states/EmptyPage.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useOrganizationTeams } from '../composables/use-organization-teams'
  import CreateTeamDialog from './CreateTeamDialog.vue'

  const router = useRouter()
  const { teams, status, createTeam } = useOrganizationTeams()
  const teamList = computed(() => teams.value ?? [])

  const {
    isOpen: isCreateOpen,
    openDialog: openCreateDialog,
    closeDialog: closeCreateDialog,
  } = useDialog()
  const createDialog = useTemplateRef('createDialog')

  const onCreate = async (body: { name: string; memberIds: string[] }) => {
    const result = await createTeam(body)
    if (result === 'invalid-name') {
      createDialog.value?.showInvalidName()
      return
    }
    if (typeof result === 'string') {
      closeCreateDialog()
      router.push({ name: 'settings-organization-team', params: { teamId: result } })
    }
  }
</script>
